// ── Stage channel ────────────────────────────────────────────────────────────
// Same-origin control bus between the admin window (presenter controls) and
// the dedicated projector window (what the room sees). Messages:
//   { type: 'hello' }                        projector announces itself
//   { type: 'beat' }                         projector heartbeat (every 3s)
//   { type: 'curtain', show }                hide/show the projected stage
//   { type: 'forecast', show, crews }        mirror the forecast (+ slider)
// With a projector window alive, the curtain applies THERE and the admin's
// own screen stays untouched; without one (mirrored display), the admin
// window falls back to covering itself.
const CHANNEL = 'parcelink-stage';

let channel = null;
const listeners = new Set();
let lastBeatAt = 0; // last heartbeat HEARD from a projector window (never our own)

function ensureChannel() {
  if (channel || typeof BroadcastChannel === 'undefined') return channel;
  channel = new BroadcastChannel(CHANNEL);
  channel.onmessage = (e) => {
    // Liveness is tracked here, on the bus itself, so ANY consumer (the
    // curtain, the session console) can ask projectorAlive() without each
    // keeping its own clock. BroadcastChannel never delivers a message to the
    // window that posted it, so a projector can't count its own beats.
    if (e.data?.type === 'beat' || e.data?.type === 'hello') lastBeatAt = Date.now();
    listeners.forEach((fn) => {
      try { fn(e.data); } catch { /* one bad listener must not kill the bus */ }
    });
  };
  return channel;
}

/** True while a separate projector window has heartbeat within `ms`. */
export function projectorAlive(ms = 8000) {
  ensureChannel();
  return Date.now() - lastBeatAt < ms;
}

// ── Projector window marker ──────────────────────────────────────────────────
// Set by ProjectorStagePage on mount; it survives that window navigating to a
// real admin page (which is what a 'live' scene asks of it). Lives here, not in
// PresentationStage, so ScreenCurtain can ask without an import cycle —
// PresentationStage re-exports both names for its existing callers.
const PROJECTOR_KEY = 'pl_is_projector';
export const markProjectorWindow = () => {
  try { sessionStorage.setItem(PROJECTOR_KEY, '1'); } catch { /* private mode */ }
};
export const isProjectorWindow = () => {
  try { return sessionStorage.getItem(PROJECTOR_KEY) === '1'; } catch { return false; }
};

export function postStage(msg) {
  const ch = ensureChannel();
  try { ch?.postMessage(msg); } catch { /* channel closed */ }
}

export function subscribeStage(fn) {
  ensureChannel();
  listeners.add(fn);
  return () => listeners.delete(fn);
}
